import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  BarChart3, MessageSquare, Mail, Loader2, Check
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Dashboard Tab
export const DashboardTab = ({ stats, loading }) => {
  const statCards = [
    { 
      label: 'Celkové návštevy', 
      value: stats.total_visits || 0, 
      icon: BarChart3,
      color: 'text-[#00BFFF]'
    },
    { 
      label: 'Celkové správy', 
      value: stats.total_messages || 0, 
      icon: MessageSquare,
      color: 'text-green-400'
    },
    { 
      label: 'Neprečítané', 
      value: stats.unread_messages || 0, 
      icon: Mail,
      color: 'text-yellow-400'
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#00BFFF]" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold mb-2">Vitajte v admin paneli</h1>
        <p className="text-zinc-400">Prehľad vašej stránky JasnéSvetlá</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {statCards.map((card, index) => (
          <div 
            key={index}
            data-testid={`stat-card-${index}`}
            className="bg-white/5 border border-white/10 rounded-xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <card.icon className={`w-8 h-8 ${card.color}`} />
            </div>
            <p className="text-3xl font-bold">{card.value}</p>
            <p className="text-zinc-400 text-sm mt-1">{card.label}</p>
          </div>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4">Posledných 7 dní</h3>
        {stats.visits_by_date && Object.keys(stats.visits_by_date).length > 0 ? (
          <div className="space-y-3">
            {Object.entries(stats.visits_by_date)
              .sort((a, b) => b[0].localeCompare(a[0]))
              .slice(0, 7)
              .map(([date, visits]) => (
                <div key={date} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                  <span className="text-zinc-400">{date}</span>
                  <span className="font-medium">{visits} návštev</span>
                </div>
              ))}
          </div>
        ) : (
          <p className="text-zinc-500">Zatiaľ žiadne dáta</p>
        )}
      </div>
    </div>
  );
};

// Stats Tab
export const StatsTab = ({ stats, loading }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#00BFFF]" />
      </div>
    );
  }

  const visitData = stats.visits_by_date ? Object.entries(stats.visits_by_date).sort((a, b) => a[0].localeCompare(b[0])) : [];
  const maxVisits = visitData.length > 0 ? Math.max(...visitData.map(([, v]) => v)) : 1;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Štatistiky návštevnosti</h1>

      <div className="bg-white/5 border border-white/10 rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-6">Návštevy za posledných 30 dní</h3>
        
        {visitData.length === 0 ? (
          <p className="text-zinc-500 text-center py-8">Zatiaľ žiadne dáta</p>
        ) : (
          <div className="space-y-3">
            {visitData.map(([date, visits]) => (
              <div key={date} className="flex items-center gap-4">
                <span className="text-sm text-zinc-400 w-24">{date}</span>
                <div className="flex-1 h-8 bg-white/5 rounded overflow-hidden">
                  <div 
                    className="h-full bg-[#00BFFF]/20 flex items-center px-3"
                    style={{ width: `${(visits / maxVisits) * 100}%` }}
                  >
                    <span className="text-sm font-medium text-[#00BFFF]">{visits}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
          <h3 className="text-lg font-semibold mb-4">Celkové štatistiky</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center py-3 border-b border-white/5">
              <span className="text-zinc-400">Celkové návštevy</span>
              <span className="text-xl font-bold text-[#00BFFF]">{stats.total_visits || 0}</span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-white/5">
              <span className="text-zinc-400">Celkové správy</span>
              <span className="text-xl font-bold">{stats.total_messages || 0}</span>
            </div>
            <div className="flex justify-between items-center py-3">
              <span className="text-zinc-400">Neprečítané správy</span>
              <span className="text-xl font-bold text-yellow-400">{stats.unread_messages || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
          <h3 className="text-lg font-semibold mb-4">Tipy na zlepšenie</h3>
          <ul className="space-y-3 text-sm text-zinc-400">
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-400 mt-0.5" />
              Pravidelne aktualizujte FAQ sekciu
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-400 mt-0.5" />
              Odpovedajte na správy do 24 hodín
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-400 mt-0.5" />
              Pridávajte nové fotky realizácií
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-400 mt-0.5" />
              Sledujte trendy v návštevnosti
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};
